<template>
  <div class="PanoramaComp r-cover z-0">
    <!-- Render Icons for Contentspots -->
    <div class="hidden">
      <SvgIcon
        v-for="csType in csTypes"
        :key="`icon-${csType}`"
        :ref="`icon-${csType}`"
        :icon="csType"
      />
    </div>
    <transition v-if="overlayActive == true" name="fade">
      <!-- Overlay -->
      <div
        :class="[ overlayActive ? 'is-active flex' : 'hidden' ]"
        class="a-cover z-0 overflow-hidden justify-center items-center z-20"
      >
        <button
          class="absolute flex-c top-4 right-4 w-12 h-12 border-accent-1 bg-white fill-accent-1 hover:bg-accent-1 hover:fill-white z-30"
          @click="toggleOverlay"
        >
          <SvgIcon
            class="w-7 h-7"
            icon="cross"
          />
        </button>
        <div class="a-cover bg-accent-1 opacity-75 z-20" />
        <div class="relative z-30 max-w-272">
          <div 
            v-if="panormaOverlayPin"
            class="absolute" 
            :style="{ left: panormaOverlayPin.horizontal + '%', top: panormaOverlayPin.vertical + '%'}"
          >
            <SvgIcon
              class="w-10 h-10 fill-accent-1 mx-auto"
              icon="map-marker"
            />
            <p class="mt-1 bg-white text-black px-3 py-2">
              {{ panormaOverlayTitle }}
            </p>
          </div>
          <img ref="panormaOverlayImage" @click.ctrl="getMapPosition" :src="panormaOverlayImage.sizes.large" :alt="panormaOverlayImage.alt" />
          <p class="bg-white px-3 py-2" v-if="mapPositionHelperActive">
            Top: {{ percentageY }} / Left: {{ percentageX }}
          </p>
        </div>
      </div>
    </transition>
    <!-- Panorama El -->
    <div
      id="pano"
      class="r-cover"
    />
    <div
      v-if="hasOverlay"
      class="absolute right-2 md:right-4 top-2 md:top-4"
    >
      <button
        class="flex-c w-12 h-12 border-accent-1 bg-white fill-accent-1 hover:bg-accent-1 hover:fill-white"
        @click="toggleOverlay"
      >
        <SvgIcon
          class="w-7 h-7"
          icon="map-marker"
        />
      </button>
    </div>
    <!-- Gyro Buttons -->
    <transition name="fade">
      <div
        v-if="showGyroBtns"
        class="absolute"
        :class="hasOverlay ? ' right-20 md:right-22 top-2 md:top-4' : 'right-2 md:right-4 top-2 md:top-4'"
      >
        <button
          v-if="showMotionControlButton"
          id="motion-control-toggle"
          :class="motionControlButtonClasses"
          :title="htmlDecode(mcLabels.btn_title)"
          :aria-label="htmlDecode(mcLabels.btn_title)"
          @click="toggleMotionControl"
        >
          <SvgIcon
            class="w-7 h-7"
            icon="gyro"
          />
        </button>
        <button
          v-if="showVirtualRealityButton"
          id="virtual-reality-enable"
          :class="virtualRealityButtonClasses"
          :title="htmlDecode(vrLabels.btn_title)"
          :aria-label="htmlDecode(vrLabels.btn_title)"
          @click="enterVr"
        >
          <SvgIcon
            class="w-7 h-7"
            icon="vr"
          />
        </button>
      </div>
    </transition>
    <!-- Slideout menu -->
    <transition name="fade">
      <Slideout
        v-if="slideoutVisible"
        :tour-group="tourGroup"
        :group-tours="groupTours"
        :pano-obj="panoObj"
        :contentspots="contentspots"
        :prev-tour="prevTour"
        :next-tour="nextTour"
        :show-slideout="showSlideout"
        @toggleSlideout="toggleSlideout"
      />
    </transition>
  </div>
</template>

<script>
/* eslint-disable camelcase */
/* global removepano: false, embedpano: false */
import SvgIcon from '@/components/SvgIcon.vue'
import Slideout from '@/components/Slideout.vue'
import { mapState, mapGetters } from 'vuex'

export default {
  name: 'Panorama',

  components: {
    SvgIcon,
    Slideout
  },

  props: {
    panoObj: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      pano: false,
      slideoutOpen: true,
      mcEnabled: false,
      vrEnabled: false,
      hasOverlay: false,
      overlayActive: false,
      mapPositionHelperActive: false,
      percentageX: null,
      percentageY: null
    }
  },

  computed: {
    // Store
    ...mapState([
      'environment',
      'settings',
      'browser',
      'isTouch',
    ]),
    ...mapGetters([
      'getTourGroupBySlug',
      'routeName',
      'routeParams',
      'isContentspot',
      'routeAoi',
      'guidedTourPage',
      'justExplorePage',
      'myStore',
    ]),
    // Environment
    isDev() { return this.environment === 'development' },
    // Browser
    isIe() { return this.browser === 'IE' },
    // Route
    // Get Group post type from slug i.e. /area-of-interest/building-1 not Just Explore
    isGroup() { return this.routeParams.media_id && this.routeParams.panorama_id },
    tourGroup() { return this.getTourGroupBySlug(this.routeParams.media_id) },
    isSingleAccess() { return this.routeParams.media_id === 'single' & this.isGroup && !this.tourGroup  },
    isGuidedTour() { return ['gt-panorama', 'gt-hotspot'].includes(this.routeName) },
    isJustExplore() { return ['je-panorama', 'je-hotspot'].includes(this.routeName) },
    // For groups of 360s
    groupTours() {
      const { guidedTourPage, justExplorePage, tourGroup, isGuidedTour, isJustExplore } = this
      // If on group post-type
      const tourGroupChildren = tourGroup && tourGroup.children
      // If Guided Tour
      const gtChildren = isGuidedTour && guidedTourPage.children
      // If 'Just Explore'
      const justExploreChildren = isJustExplore && justExplorePage.children
      // Single-access needs a fallback
      const children = (tourGroupChildren || gtChildren || justExploreChildren) || []
      // Return tour arr
      return children.filter(({ type }) => type === 'panorama')
    },
    tourContext() {
      const { isGuidedTour, groupTours, panoObj } = this
      if (!groupTours) { return }
      const groupLoops = !isGuidedTour
      const groupSlugs = groupTours.map(item => item.slug)
      const tourIndex = groupSlugs.indexOf(panoObj.slug)
      const prevI = tourIndex - 1
      const nextI = tourIndex + 1
      const lastI = groupTours.length - 1
      const tourIsFirst = tourIndex === 0
      const tourIsLast = tourIndex === lastI
      const prev = !tourIsFirst ? groupTours[prevI] : groupLoops ? groupTours[lastI] : {}
      const next = !tourIsLast ? groupTours[nextI] : groupLoops ? groupTours[0] : {}
      return { tourIndex, prev, next }
    },
    prevTour() { return this.tourContext && this.tourContext.prev },
    nextTour() { return this.tourContext && this.tourContext.next },
    // Contentspots
    contentspots() {
      // Get Route name for contentspots i.e. aoi-contentspots, je-contentspots, gt-contentspots
      const csRouteName = this.routeName.replace('panorama', 'contentspot')
      // Get clone of children before modifying
      const csArr = ((this.panoObj || {}).children || []).slice()
      // Modify to apply a route name for this context
      return csArr.map((item) => {
        item.routeTo = {
          name: csRouteName,
          params: {
            contentspot_id: item.slug
          }
        }
        return item
      })
    },
    csTypes() {
      return this.contentspots
        .map(({ type }) => type)
        .filter((value, index, self) => self.indexOf(value) === index)
    },
    // Gyro Buttons
    isMcAdminEnabled() { return this.myStore(['settings', 'motion_control_enabled']) ?? true },
    isVrAdminEnabled() { return this.myStore(['settings', 'virtual_reality_enabled']) || this.isDev },
    gyroBtnsStyles() { return this.myStore(['styles', 'panorama_screen', 'gyro_buttons']) ?? {} },
    gyroBtnsActiveStyles() { return this.gyroBtnsStyles.active ?? false },
    gyroBtnsClasses() {
      const { gyroBtnsStyles } = this
      const { focus, hover } = gyroBtnsStyles || {}
      const focusStyles = focus || {}
      const hoverStyles = hover || {}
      // Border Width
      const borderWidthVal = gyroBtnsStyles.border_width ?? '2'
      const borderWidthClass = `border-${borderWidthVal}`
      // Border Colors
      const borderColorFocusVal = focusStyles.border_color ?? 'black'
      const borderColorFocusClass = `focus:border-${borderColorFocusVal}`
      const borderColorHoverVal = hoverStyles.border_color ?? 'accent-1'
      const borderColorHoverClass = `lg:hover:border-${borderColorHoverVal}`
      // Border Radius
      const borderRadiusVal = gyroBtnsStyles.border_radius ?? '2'
      const borderRadiusClass = borderRadiusVal ? `rounded-${borderRadiusVal}` : ''
      // Background Colors
      const bgColorFocusVal = focusStyles.bg_color ?? 'white'
      const bgColorFocusClass = `focus:bg-${bgColorFocusVal}`
      const bgColorHoverVal = hoverStyles.bg_color ?? 'accent-1'
      const bgColorHoverClass = `lg:hover:bg-${bgColorHoverVal}`
      // Fill Colors
      const fillColorFocusVal = focusStyles.fill_color ?? 'black'
      const fillColorFocusClass = `focus:fill-${fillColorFocusVal}`
      const fillColorHoverVal = hoverStyles.fill_color ?? 'white'
      const fillColorHoverClass = `lg:hover:fill-${fillColorHoverVal}`
      // Return
      return [
        // Dimensions
        'w-12 h-12',
        // Margins
        'mb-2 last:mb-0',
        // Display
        'flex-c',
        // Border
        borderWidthClass,
        borderColorFocusClass,
        borderColorHoverClass,
        borderRadiusClass,
        // Bg
        bgColorFocusClass,
        bgColorHoverClass,
        // Fill
        fillColorFocusClass,
        fillColorHoverClass,
        // Outline
        'focus:outline-none',
        // Transitions
        'transition-colors duration-500 ease-in-out'
      ]
    },
    mcLabels() { return this.myStore(['labels', 'panorama_screen', 'motion_control_button']) ?? false },
    showMotionControlButton() { return this.isMcAdminEnabled && this.mcLabels },
    motionControlButtonClasses() {
      const { mcEnabled, gyroBtnsStyles, gyroBtnsActiveStyles, gyroBtnsClasses } = this
      const styleObj = mcEnabled ? gyroBtnsActiveStyles : gyroBtnsStyles
      const { border_color, bg_color, fill_color } = styleObj || {}
      const gtmClass = `motion-control-${mcEnabled ? 'enabled' : 'disabled'}`
      const borderClass = `border-${border_color || 'accent-1'}`
      const backgroundClass = `bg-${bg_color || 'white'}`
      const fillClass = `fill-${fill_color || 'accent-1'}`
      // Return
      return [
        gtmClass,
        borderClass,
        backgroundClass,
        fillClass,
        gyroBtnsClasses,
      ]
    },
    vrLabels() { return this.myStore(['labels', 'panorama_screen', 'virtual_reality_button']) ?? false },
    showVirtualRealityButton() { return this.isVrAdminEnabled && this.vrLabels },
    virtualRealityButtonClasses() {
      const { vrEnabled, gyroBtnsStyles, gyroBtnsActiveStyles, gyroBtnsClasses } = this
      const styleObj = vrEnabled ? gyroBtnsActiveStyles : gyroBtnsStyles
      const { border_color, bg_color, fill_color } = styleObj || {}
      const gtmClass = `virtual-reality-${vrEnabled ? 'enabled' : 'disabled'}`
      const borderClass = `border-${border_color || 'accent-1'}`
      const backgroundClass = `bg-${bg_color || 'white'}`
      const fillClass = `fill-${fill_color || 'accent-1'}`
      // Return
      return [
        gtmClass,
        borderClass,
        backgroundClass,
        fillClass,
        gyroBtnsClasses,
      ]
    },
    showGyroBtns() {
      const { isTouch, isContentspot, isDev, gyroBtnsStyles, showMotionControlButton, showVirtualRealityButton } = this
      const isTouchOrDev = isTouch || isDev
      const hasRequiredData = gyroBtnsStyles && (showMotionControlButton || showVirtualRealityButton)
      return isTouchOrDev && hasRequiredData && !isContentspot
    },
    // Slideout
    slideoutVisible() { return !this.isContentspot },
    showSlideout() { return (this.slideoutOpen && !this.isContentspot) ? true : false },
    panormaOverlayImage() { return this.panoObj.panorama_location_overlay.image },
    panormaOverlayTitle() { return this.panoObj.panorama_location_overlay.children[0].text },
    panormaOverlayPin() { 
      const pinPosition = this.panoObj.panorama_location_overlay.children[0].position
      const vertical = pinPosition.vertical
      const horizontal = pinPosition.horizontal
      return { vertical, horizontal }
    },
  },

  watch: {
    panoObj() {
      this.krpTour()
      this.updatePageMeta()
      this.hasOverlayContent()
    }
  },

  created() {
    this.updatePageMeta()
    this.hasOverlayContent()
  },

  mounted() {
    embedpano({
      xml: 'vendor/krpano.xml',
      target: 'pano',
      id: 'rama',
      mobilescale: 1.0,
      onready: this.krpReady,
      consolelog: this.isDev
    })
  },

  beforeDestroy() {
    removepano('rama')
    this.pano = false
  },

  methods: {
    // Page title
    updatePageMeta() {
      if (!this.panoObj) { return }
      const newPageMeta = { pageTitle: this.panoObj.title }
      const groupTitle = this.tourGroup && this.tourGroup.title
      const gtTitle = this.isGuidedTour && this.guidedTourPage && this.guidedTourPage.title
      const jeTitle = this.isJustExplore && this.justExplorePage && this.justExplorePage.title
      const aoiTitle = this.routeAoi && this.routeAoi.title
      newPageMeta.parentTitle = groupTitle || gtTitle || jeTitle || aoiTitle
      this.$store.commit('pageMeta', newPageMeta)
    },
    toggleSlideout() { return this.slideoutOpen = !this.slideoutOpen },
    // Panorama functionality
    krpReady(rama) {
      this.pano = rama
      window.onSpotLoaded = this.onSpotLoaded
      window.onSpotClick = this.onSpotClick
      window.onEnterVr = this.onEnterVr
      window.onExitVr = this.onExitVr
      /*window.vrNext = this.vrNext
      window.vrPrevious = this.vrPrevious*/

      this.krpTour()
    },
    krpTour() {
      if (!this.pano) { return }
      const { panoObj } = this
      const { panorama, position, corners } = panoObj
      const { horizontal, vertical } = position || {}
      const { left, right, up, down } = corners || {}
      const dateNow = Date.now()
      // View
      const fov = 95
      const fovMin = 65
      const fovMax =  115
      const startAth = horizontal || 0
      const startAtv = vertical || 0
      const minAth = left || 0
      const maxAth = right || 360
      const minAtv = up || -90
      const maxAtv = down || 90
      // Image
      const { sizes, url } = panorama || {}
      const { large } = sizes || {}
      const previewUrl = large
      const sphereUrl = url
      if (!previewUrl || !sphereUrl) { return false }

      const krpScene = `<krpano><view fov="${fov}" fovmin="${fovMin}" fovmax="${fovMax}" limitview="lookat" hlookat="${startAth}" vlookat="${startAtv}" hlookatmin="${minAth}" hlookatmax="${maxAth}" vlookatmin="${minAtv}" vlookatmax="${maxAtv}"/><preview url="${previewUrl}?nocache=${dateNow}"/><image><sphere url="${sphereUrl}"/></image></krpano>`

      const krpCall = `loadxml(${krpScene}, null, MERGE, blend(1, linear)); ${this.getContentspotXml().join('')}`

      this.pano.call(krpCall)
    },
    // Contentspots
    getContentspotSlug(postId) { return `tour-${this.panoObj.id}-contentspot-${postId}` },
    getContentspotXml() {
      return this.contentspots.map((item, i1) => {
        const { position } = item
        const csSlug = this.getContentspotSlug(item.id)
        return `
          addhotspot(${csSlug});
          set(hotspot[${csSlug}].ath, ${position.horizontal});
          set(hotspot[${csSlug}].atv, ${position.vertical});
          set(hotspot[${csSlug}].type, text);
          set(hotspot[${csSlug}].html, '');
          set(hotspot[${csSlug}].padding, 0);
          set(hotspot[${csSlug}].zoom, false);
          set(hotspot[${csSlug}].bg, false);
          set(hotspot[${csSlug}].interactivecontent, false);
          set(hotspot[${csSlug}].renderer, css3d);
          set(hotspot[${csSlug}].onloaded, js( onSpotLoaded('${escape(JSON.stringify(item))}') ));
          set(hotspot[${csSlug}].onclick, js( onSpotClick('${escape(JSON.stringify(item.routeTo))}') ));
        `
      })
    },
    onSpotLoaded(csObjString) {
      const csObj = JSON.parse(unescape(csObjString))
      const item = false
      const { $refs, myStore } = this
      const csStyles = myStore(['styles', 'contentspot']) || {}
      const iconStyles = csStyles.icon || false
      const tooltipStyles = csStyles.tooltip || false
      if (!iconStyles || !tooltipStyles) { return }
      // Contentspot - Unique Slug
      const csSlug = this.getContentspotSlug(csObj.id)
      const hotspotObj = this.pano.get(`hotspot[${csSlug}]`)
      const $hotspot = hotspotObj && hotspotObj.sprite
      if (!$hotspot) { return }
      // Hotspot Classes
      // Icon HTML
      const iconType = csObj.type
      const iconRef = $refs && $refs[`icon-${csObj.type}`]
      const iconItem = iconRef.length && iconRef[0]
      const $iconHtml = iconItem && iconItem.$el && iconItem.$el.cloneNode(true)
      if (!$iconHtml) { return }
      $iconHtml.classList.add('w-7')
      $iconHtml.classList.add('xl:w-9')
      $iconHtml.classList.add('h-7')
      $iconHtml.classList.add('xl:h-9')
      // Icon
      const iconClasses = ['icon', 'r-cover z-1 overflow-hidden', 'flex-c', `border-${iconStyles.border_color} border-${iconStyles.border_width} rounded-full`, `group-focus:border-${iconStyles.focus.border_color} lg:group-hover:border-${iconStyles.hover.border_color}`, `bg-${iconStyles.bg_color}`, `group-focus:bg-${iconStyles.focus.bg_color} lg:group-hover:bg-${iconStyles.hover.bg_color}`, `fill-${iconStyles.fill_color}`, `group-focus:fill-${iconStyles.focus.fill_color} lg:group-hover:fill-${iconStyles.hover.fill_color}`, 'transition-colors duration-500 ease-in-out', 'pointer-events-none']
      const $icon = document.createElement('div')
      $icon.className = iconClasses.join(' ')
      $icon.appendChild($iconHtml)
      // Tooltip span
      const allowWrap = csObj.title.length >= 30 || this.isIe
      const tooltipWhitespace = allowWrap ? 'whitespace-normal' : 'whitespace-no-wrap'
      const $tooltipSpan = document.createElement('span')
      $tooltipSpan.className = `tooltip-text w-full overflow-hidden block leading-none lg:leading-thin ${tooltipWhitespace} ${tooltipStyles.text_transform}`
      $tooltipSpan.appendChild( document.createTextNode( this.htmlDecode(csObj.title) ) )
      // Tooltip
      const tooltipWidth = allowWrap ? 'w-64' : 'max-w-64'
      const tooltipClasses = ['tooltip', 'absolute left-5 xl:left-7_5 top-0', `${tooltipWidth} h-12 xl:h-15`, 'pl-9 xl:pl-11_5 pr-2 xl:pr-4', 'flex items-center', 'rounded-r-full', `bg-${tooltipStyles.bg_color}`, `text-${tooltipStyles.text_color}`, 'text-center', 'pointer-events-none', 'opacity-0', 'group-focus:opacity-100 lg:group-hover:opacity-100', 'transition-opacity duration-500 ease-in-out']
      const $tooltip = document.createElement('div')
      $tooltip.setAttribute('class', tooltipClasses.join(' '))
      $tooltip.appendChild($tooltipSpan)
      // Button
      const wrapperClasses = [
        // GTM
        'hotspot',
        'hotspot-contentspot',
        `hotspot-contentspot-${iconType}`,
        // Styles
        'absolute left-0 top-0', 'w-12 xl:w-15 h-12 xl:h-15', 'transform-translate-x--1/2-y--1/2 z-0', 'block', 'rounded-full'
      ]
      const $wrapper = document.createElement('div')
      $wrapper.setAttribute('id', `${csSlug}-btn`)
      $wrapper.setAttribute('class', wrapperClasses.join(' '))
      $wrapper.appendChild($icon)
      $wrapper.appendChild($tooltip)

      const hotspotClasses = ['group', 'w-12 xl:w-15 h-12 xl:h-15']
      $hotspot.setAttribute('id', `${csSlug}-container`)
      $hotspot.setAttribute('class', hotspotClasses.join(' '))
      $hotspot.setAttribute('title', this.htmlDecode(csObj.title))
      $hotspot.setAttribute('aria-label', this.htmlDecode(csObj.title))
      $hotspot.setAttribute('tabindex', '0')
      $hotspot.appendChild($wrapper)
      const clickCodes = [13, 32]
      $hotspot.addEventListener('keyup', ({ which }) => {
        const isClick = clickCodes.includes(which)
        if (isClick) { this.onSpotClick(escape(JSON.stringify(csObj.routeTo))) }
      })
    },
    onSpotClick(routeToString) {
      const routeTo = JSON.parse(unescape(routeToString))
      if (!routeTo) { return }
      this.$router.push(routeTo)
    },
    toggleMotionControl() {
      if (!this.pano) { return }
      const isEnabled = this.pano.get('plugin[gyro].enabled')
      if (!isEnabled) {
        this.pano.call('set(plugin[gyro].enabled, true)')
        this.mcEnabled = true
      } else {
        this.pano.call('set(plugin[gyro].enabled, false)')
        this.mcEnabled = false
      }
    },
    enterVr() {
      if (!this.pano) { return }
      this.pano.call('webvr.toggleVR()')
    },
    onEnterVr() {
      if (!this.pano) { return }
      this.pano.call('set(events[vr_events].onviewchange, followMenuAction()); set(hotspot[followMenuPrev].visible, true); set(hotspot[followMenuNext].visible, true);')
    },
    onExitVr() {
      if (!this.pano) { return }
      this.pano.call('set(events[vr_events].onviewchange, null); set(hotspot[followMenuPrev].visible, false); set(hotspot[followMenuNext].visible, false)')
    },
    /*vrNext() {
    },
    vrPrevious() {
    },*/
    toggleOverlay() {
      if(this.overlayActive == false) {
        this.overlayActive = true
      } else {
        this.overlayActive = false
      }
    },
    getMapPosition(e) {
      const rect = this.$refs.panormaOverlayImage.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const percentageX = (x / this.$refs.panormaOverlayImage.clientWidth) * 100
      const percentageY = (y / this.$refs.panormaOverlayImage.clientHeight) * 100
      this.mapPositionHelperActive = true
      this.percentageX = Math.round(percentageX)
      this.percentageY = Math.round(percentageY)
    },
    hasOverlayContent() {
      if(this.panoObj.panorama_location_overlay) {
        this.hasOverlay = true
      } else {
        this.hasOverlay = false
      }
    }
  }
}
</script>
